import React from "react"
import { twMerge } from "tailwind-merge"

import placeholderImage from "../../../../assets/images/profile-photo-placeholder.jpg"
import { formatDurationWithSessions } from "../../../utils/utils"
import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { generateColorPalette } from "../utils/enhancedColorUtils"
import { getThemeShapeClasses } from "../utils/themeUtils"
import { buildServiceUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const ServicesSectionMobile = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "list"
  const selectedServiceIds = settings.selectedServiceIds || []

  // Get theme colors with fallback generation
  const getThemeColors = () => {
    if (!theme?.colors) return { bgLight: "#FFFFFF", bgMedium: "#E6DAD7", bgExtraLight: "#F2EEEC" }

    const colors = { ...theme.colors }

    // If bgMedium, bgExtraLight, or bgLight are missing, generate them from primary color
    if (!colors.bgMedium || !colors.bgExtraLight || !colors.bgLight) {
      const primaryColor = colors.primary
      if (primaryColor) {
        const generatedPalette = generateColorPalette(primaryColor)
        colors.bgMedium = colors.bgMedium || generatedPalette.bgMedium
        colors.bgExtraLight = colors.bgExtraLight || generatedPalette.bgExtraLight
        colors.bgLight = colors.bgLight || generatedPalette.bgLight
      }
    }

    return colors
  }

  const themeColors = getThemeColors()

  // Resolve background color based on toggle setting
  const resolveSectionBackgroundColor = () => {
    // Check if background color toggle is enabled (handle both boolean and string values)
    const showBgColor = settings.showBackgroundColor === true || settings.showBackgroundColor === "true"
    const bgColorIsTheme = settings.backgroundColor === "theme"

    if (showBgColor && bgColorIsTheme) {
      // Use bgLight from theme colors
      return themeColors.bgLight || theme?.colors?.bgLight || "#FFFFFF"
    }
    // Default to white if toggle is off or not set
    return "#FFFFFF"
  }

  const sectionBackgroundColor = resolveSectionBackgroundColor()

  const handleServiceClick = (serviceId) => {
    window.location.href = buildServiceUrl(serviceId, practice)
  }

  // Get practice services - filter to only show public services
  const allPracticeServices = (practice?.services || []).filter((service) => service.public === true)

  // If specific services are selected, use those in the specified order; otherwise use all services
  const selectedServices =
    selectedServiceIds.length > 0
      ? selectedServiceIds.map((id) => allPracticeServices.find((service) => service.id === id)).filter(Boolean)
      : allPracticeServices

  // Show all selected services (no limits)
  const allItems = selectedServices

  const formatPrice = (amountCents, currency) => {
    if (amountCents === 0) {
      return "Free"
    }
    const amount = amountCents / 100
    return `$${amount.toFixed(2)}${currency === "CAD" ? " CAD" : ""}`
  }

  // Render service card for mobile (vertical stack with image on top)
  const renderMobileServiceCard = (item, index) => {
    const price =
      typeof item.amountCents === "number"
        ? item.amountCents === 0
          ? "FREE"
          : formatPrice(item.amountCents, practice?.defaultCurrency || "USD")
        : ""
    const duration = item.timeLength ? formatDurationWithSessions(item.timeLength, item) : ""

    // Format location and service type information
    const serviceType = item.virtual ? "Virtual" : "In-person"
    const location = practice?.location?.city ? `${practice.location.city}, ${practice.location.state}` : ""
    const locationType = item.atHome || item.at_home ? "At your home" : ""
    const locationInfo = [serviceType, location, locationType].filter(Boolean).join(" • ")

    // Get image URL from various possible sources
    const imageUrl =
      item.imageUrl ||
      item.mediumWebpUrl ||
      (item.photoHandles?.large?.webp ? `https://cdn.filestackcontent.com/${item.photoHandles.large.webp}` : null) ||
      (item.photoHandles?.original?.any ? `https://cdn.filestackcontent.com/${item.photoHandles.original.any}` : null)

    // Truncate description for display
    const displayDescription =
      item.description && item.description.length > 200 ? item.description.substring(0, 200) + "..." : item.description

    return (
      <div key={index} className="flex h-[500px] w-full flex-col items-start justify-start gap-4">
        {/* Image - Full width with aspect ratio */}
        <div className={twMerge("aspect-[396/250] w-full overflow-hidden bg-gray-100", getThemeShapeClasses(theme))}>
          <img
            src={imageUrl || placeholderImage}
            alt={item.name}
            className="h-full w-full object-cover"
            onError={(e) => {
              if (e.target.src !== placeholderImage) e.target.src = placeholderImage
            }}
          />
        </div>

        {/* Content - Fixed structure with consistent height - Left aligned text */}
        <div className="flex w-full flex-1 flex-col items-start justify-start gap-2 overflow-hidden text-left">
          {/* Price - First */}
          {price && (
            <div
              className="w-full truncate text-[18px] font-semibold leading-[24px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphBoldWeight || "600",
                color: theme?.colors?.textDark || "#55433F"
              }}>
              {price}
            </div>
          )}

          {/* Title - Second - Single line with ellipsis */}
          <div className="w-full">
            <div
              className="w-full truncate text-[22px] font-bold leading-[32px]"
              style={{
                fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: theme?.colors?.textDark || "#000000"
              }}>
              {item.name}
            </div>
          </div>

          {/* Duration and Location - Third */}
          {(duration || locationInfo) && (
            <div
              className="w-full truncate text-[14px] font-normal leading-[20px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.textDark || "#55433F"
              }}>
              {duration && locationInfo ? `${duration} • ${locationInfo}` : duration || locationInfo}
            </div>
          )}

          {/* Description - Fourth - Limited to 2 lines */}
          <div className="w-full">
            {displayDescription && (
              <div
                className="prose prose-lg line-clamp-2 w-full max-w-none text-[16px] font-normal leading-[24px]"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                  color: theme?.colors?.textDark || "#000000",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden"
                }}
                dangerouslySetInnerHTML={{ __html: displayDescription }}></div>
            )}
          </div>

          {/* Details button - Close to content */}
          <div
            className="mt-4 flex cursor-pointer items-center justify-start gap-3 px-8 py-3 transition-opacity hover:opacity-90"
            style={{
              backgroundColor: theme?.colors?.primary || "#BD654F",
              borderRadius: theme?.borderRadius || "12px"
            }}
            onClick={() => handleServiceClick(item.id)}>
            <div
              className="text-[16px] font-normal leading-[24px] text-white"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.bgLight || "#F4ECE9"
              }}>
              Details
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <section
      className={twMerge("relative py-8 pb-[72px]", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{
        backgroundColor: sectionBackgroundColor
      }}>
      <AnimatedSection className="mx-auto max-w-[1160px] px-5">
        <div className="flex w-full flex-col items-start gap-16">
          {/* Header */}
          <div className="flex w-full flex-col items-start gap-8">
            {/* Title and Browse Services Button */}
            <div className="flex w-full flex-col items-start gap-6">
              <div className="flex w-full items-center justify-between gap-4">
                <h2
                  className="text-left text-[28px] font-bold leading-[32px]"
                  style={{
                    fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                    fontWeight: theme?.fonts?.headingWeight || "700",
                    color: theme?.colors?.textDark || "#55433F"
                  }}>
                  {section.headline || "Services"}
                </h2>

                {/* Browse Services Button - Not shown on services section (this is the services page) */}
              </div>

              {section.body && (
                <p
                  className="max-w-3xl text-left text-[18px] leading-[24px]"
                  style={{
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                    color: theme?.colors?.textDark || "#55433F"
                  }}>
                  {section.body}
                </p>
              )}
            </div>
          </div>

          {/* Services Grid */}
          {allItems.length > 0 && (
            <div className="w-full">
              {layout === "highlights_2" || layout === "highlights_3" ? (
                // Highlights layout - grid for mobile with equal height
                <div
                  className={`grid items-stretch gap-6 ${
                    layout === "highlights_2" ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"
                  }`}>
                  {allItems.map((item, index) => renderMobileServiceCard(item, index))}
                </div>
              ) : (
                // List layout - vertical stack
                <div className="flex flex-col gap-10">
                  {allItems.map((item, index) => renderMobileServiceCard(item, index))}
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {allItems.length === 0 && (
            <div className="flex flex-col items-center gap-6 py-16 text-center">
              <div className="text-6xl">🔧</div>
              <h3
                className="text-2xl font-semibold"
                style={{
                  fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                  color: theme?.colors?.textDark || "#55433F"
                }}>
                No services available
              </h3>
              <p
                className="text-lg text-gray-600"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                }}>
                {practice?.services?.length === 0
                  ? "This practice hasn't added any services yet."
                  : "No services match the current selection."}
              </p>
            </div>
          )}

          {/* Show More Button - Not needed for Services section (shows all services) */}
        </div>
      </AnimatedSection>
    </section>
  )
}

export default ServicesSectionMobile
